/**
 * Symbol Search Service
 *
 * Hybrid search strategy for instant autocomplete:
 * 1. Try local symbol cache (Redis-backed, <10ms)
 * 2. Fallback to FMP API proxy (~500ms)
 *
 * SECURITY: Uses backend proxy (fmp_proxy Cloud Function) to keep API key server-side.
 * This matches the secure pattern used by fmpDataService.ts.
 *
 * NOTE: Filters results to supported exchanges (US, ASX, LSE, SGX, HKSE, JPX).
 * Unsupported exchanges are filtered out to prevent users from selecting unavailable stocks.
 */

import {
  searchSymbols as cacheSearch,
  getTopCompaniesByMarketCap,
  SearchResult as CacheSearchResult
} from './symbolCacheService';
import { gatewayFetch } from './gateway';
import { logger } from './loggingService';

export interface SearchResult {
  symbol: string;
  name: string;
  exchange: string;
  exchangeDisplay: string;
  type: string;
  isEtf?: boolean;  // NEW: Indicates if symbol is an ETF
  currency?: string;
  sector?: string;
  industry?: string;
}

// Cache for search results to reduce API calls
const searchCache = new Map<string, { results: SearchResult[]; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 1000; // Prevent unbounded memory growth

// Supported exchanges — keep in sync with functions/utils/exchange_utils.py
const SUPPORTED_EXCHANGES = [
  'NASDAQ', 'NYSE', 'AMEX', 'NYSEARCA', 'BATS', 'OTC', 'PNK',  // US
  'ASX',   // Australia
  'LSE',   // London
  'SGX',   // Singapore
  'HKSE',  // Hong Kong
  'JPX',   // Tokyo
];

/**
 * Search for stocks by company name or ticker symbol.
 * Hybrid strategy: Try local cache first (instant), fallback to FMP API.
 */
export async function searchSymbols(query: string): Promise<SearchResult[]> {
  if (!query || query.length < 1) {
    return [];
  }

  // Gather results from both cache and FMP API, then merge
  let cacheResults: SearchResult[] = [];
  try {
    const results = await cacheSearch(query, undefined, 10);
    if (results && results.length > 0) {
      cacheResults = results.map(convertCacheResult);
    }
  } catch (error) {
    logger.warn('[SymbolSearch] Cache search failed', { error });
  }

  // Always query FMP API to catch ASX/international stocks not in cache
  const fmpResults = await searchSymbolsFMP(query);

  // Merge: cache results first, then FMP results (deduplicated)
  const seenSymbols = new Set(cacheResults.map(r => r.symbol));
  const merged = [...cacheResults];
  for (const r of fmpResults) {
    if (!seenSymbols.has(r.symbol)) {
      seenSymbols.add(r.symbol);
      merged.push(r);
    }
  }

  return merged.slice(0, 10);
}

/**
 * Get top companies by market cap (used for empty search).
 */
export async function getTopCompanies(
  sector?: string,
  limit?: number
): Promise<SearchResult[]> {
  try {
    const cacheResults = await getTopCompaniesByMarketCap(sector, limit);
    return cacheResults.map(convertCacheResult);
  } catch (error) {
    logger.error('[SymbolSearch] Error getting top companies', { error });
    return [];
  }
}

/**
 * Convert cache search result to symbol search result format.
 */
function convertCacheResult(result: CacheSearchResult): SearchResult {
  return {
    symbol: result.symbol,
    name: result.name,
    exchange: result.exchange,
    exchangeDisplay: mapExchangeCode(result.exchange),
    type: 'stock',
    isEtf: false,  // Cache service doesn't have ETF data yet
    sector: result.sector,
  };
}

/**
 * Legacy FMP API search (fallback when cache unavailable).
 * Uses two endpoints in parallel:
 * - /stable/search-symbol - searches by ticker symbol prefix
 * - /stable/search-name - searches by company name
 */
async function searchSymbolsFMP(query: string): Promise<SearchResult[]> {
  if (!query || query.length < 1) {
    return [];
  }

  const cacheKey = query.toLowerCase();
  const cached = searchCache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.results;
  }

  try {
    // Call both endpoints in parallel for comprehensive search
    const [symbolResults, nameResults] = await Promise.all([
      fetchSearchSymbol(query),
      fetchSearchName(query),
    ]);

    // Merge results, prioritizing symbol matches and removing duplicates
    const seenSymbols = new Set<string>();
    const mergedResults: SearchResult[] = [];

    // Add symbol matches first (more relevant for exact ticker searches)
    for (const result of symbolResults) {
      if (!seenSymbols.has(result.symbol)) {
        seenSymbols.add(result.symbol);
        mergedResults.push(result);
      }
    }

    // Add name matches
    for (const result of nameResults) {
      if (!seenSymbols.has(result.symbol)) {
        seenSymbols.add(result.symbol);
        mergedResults.push(result);
      }
    }

    // Evict stale/overflow entries before caching
    if (searchCache.size >= MAX_CACHE_SIZE) {
      const now = Date.now();
      const keysToDelete: string[] = [];
      searchCache.forEach((entry, key) => {
        if (now - entry.timestamp >= CACHE_TTL) keysToDelete.push(key);
      });
      keysToDelete.forEach(k => searchCache.delete(k));
      // If still over limit, drop oldest
      if (searchCache.size >= MAX_CACHE_SIZE) {
        const entries: Array<[string, { results: SearchResult[]; timestamp: number }]> = [];
        searchCache.forEach((v, k) => entries.push([k, v]));
        entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
        const removeCount = searchCache.size - MAX_CACHE_SIZE + 1;
        for (let i = 0; i < removeCount; i++) searchCache.delete(entries[i][0]);
      }
    }
    searchCache.set(cacheKey, { results: mergedResults, timestamp: Date.now() });

    return mergedResults;
  } catch (error) {
    logger.error('[SymbolSearch] FMP search error', { error });
    return [];
  }
}

/**
 * Search by ticker symbol prefix using FMP proxy (secure - API key server-side)
 */
async function fetchSearchSymbol(query: string): Promise<SearchResult[]> {
  try {
    const response = await gatewayFetch('fmp', 'search-symbol', { auth: 'none', params: { query, limit: 10 } });

    if (!response.ok) {
      logger.warn('[SymbolSearch] FMP search-symbol API error', { status: response.status });
      return [];
    }

    const data = await response.json();
    return transformResults(data);
  } catch (error) {
    logger.error('[SymbolSearch] FMP search-symbol fetch error', { error });
    return [];
  }
}

/**
 * Search by company name using FMP proxy (secure - API key server-side)
 */
async function fetchSearchName(query: string): Promise<SearchResult[]> {
  try {
    const response = await gatewayFetch('fmp', 'search-name', { auth: 'none', params: { query, limit: 10 } });

    if (!response.ok) {
      logger.warn('[SymbolSearch] FMP search-name API error', { status: response.status });
      return [];
    }

    const data = await response.json();
    return transformResults(data);
  } catch (error) {
    logger.error('[SymbolSearch] FMP search-name fetch error', { error });
    return [];
  }
}

/**
 * Transform FMP API response to SearchResult array
 * Filters to supported exchanges only
 */
function transformResults(data: any): SearchResult[] {
  if (!Array.isArray(data)) {
    return [];
  }

  return data
    .filter((item: any) => {
      // Must have symbol and name
      if (!item.symbol || !item.name) return false;

      // Filter to supported exchanges
      const exchange = (item.exchange || item.exchangeShortName || item.stockExchange || '').toUpperCase();
      return SUPPORTED_EXCHANGES.includes(exchange);
    })
    .map((item: any) => ({
      symbol: item.symbol,
      name: item.name,
      exchange: item.exchange || item.stockExchange || item.exchangeShortName || '',
      exchangeDisplay: mapExchangeCode(item.exchange || item.exchangeShortName || item.stockExchange || ''),
      type: 'stock',
      isEtf: item.isEtf || false,  // NEW: FMP API provides isEtf flag
      currency: item.currency,
      sector: item.sector || '',
      industry: item.industry || '',
    }));
}

/**
 * Map FMP exchange codes to human-readable names
 */
function mapExchangeCode(code: string): string {
  const exchangeMap: Record<string, string> = {
    // US Markets
    'NASDAQ': 'NASDAQ',
    'NYSE': 'NYSE',
    'AMEX': 'NYSE American',
    'NYSEARCA': 'NYSE Arca',
    'BATS': 'BATS',
    'OTC': 'OTC',
    'PNK': 'Pink Sheets',

    // Hong Kong
    'HKSE': 'HKEX',
    'HKG': 'HKEX',

    // Singapore
    'SGX': 'SGX',
    'SES': 'SGX',

    // Australia
    'ASX': 'ASX',

    // London
    'LSE': 'LSE',
    'LON': 'LSE',

    // Japan
    'TSE': 'TSE',
    'TYO': 'TSE',
    'JPX': 'JPX',

    // Canada
    'TSX': 'TSX',
    'TOR': 'TSX',
    'TSXV': 'TSX-V',

    // Germany/Europe
    'FRA': 'Frankfurt',
    'XETRA': 'XETRA',
    'ETR': 'XETRA',
    'EURONEXT': 'Euronext',
    'PAR': 'Euronext Paris',

    // China
    'SHE': 'Shenzhen',
    'SHA': 'Shanghai',
    'SHZ': 'Shenzhen',
    'SHH': 'Shanghai',
  };

  return exchangeMap[code] || code;
}

/**
 * Validation result for symbol/exchange pair
 */
export interface ValidationResult {
  valid: boolean;
  symbol: string;
  exchange?: string;
  suggestion?: string;  // Suggested symbol if invalid
  error?: string;       // Error message if invalid
}

/**
 * Validate a symbol exists and optionally matches the specified exchange
 * Uses FMP search API to verify the symbol is valid
 */
export async function validateSymbol(symbol: string, exchange?: string): Promise<ValidationResult> {
  if (!symbol || symbol.trim().length === 0) {
    return {
      valid: false,
      symbol,
      error: 'Symbol is required'
    };
  }

  const normalizedSymbol = symbol.trim().toUpperCase();

  try {
    // Search for exact symbol match using FMP proxy (secure - API key server-side)
    const response = await gatewayFetch('fmp', 'search-symbol', { auth: 'none', params: { query: normalizedSymbol, limit: 10 } });

    if (!response.ok) {
      logger.warn('[SymbolSearch] FMP validation API error', { status: response.status });
      // On API error, allow the symbol through (fail open for better UX)
      return { valid: true, symbol: normalizedSymbol };
    }

    const data = await response.json();

    if (!Array.isArray(data) || data.length === 0) {
      return {
        valid: false,
        symbol: normalizedSymbol,
        error: `Symbol "${normalizedSymbol}" not found`
      };
    }

    // Look for exact symbol match (case-insensitive)
    const exactMatch = data.find((item: any) =>
      item.symbol?.toUpperCase() === normalizedSymbol
    );

    if (exactMatch) {
      const matchExchange = (exactMatch.exchange || exactMatch.exchangeShortName || '').toUpperCase();

      // If exchange specified, check if it matches
      if (exchange) {
        const normalizedExchange = exchange.toUpperCase();
        // Allow some flexibility in exchange matching (NYSE vs NYSEARCA, etc.)
        const exchangeMatches = matchExchange === normalizedExchange ||
          matchExchange.includes(normalizedExchange) ||
          normalizedExchange.includes(matchExchange);

        if (!exchangeMatches && SUPPORTED_EXCHANGES.includes(matchExchange)) {
          // Symbol exists but on different US exchange - valid but note the correct exchange
          return {
            valid: true,
            symbol: normalizedSymbol,
            exchange: matchExchange,
            suggestion: `Found on ${mapExchangeCode(matchExchange)} instead of ${exchange}`
          };
        }
      }

      // Check if it's a supported exchange
      if (!SUPPORTED_EXCHANGES.includes(matchExchange)) {
        return {
          valid: false,
          symbol: normalizedSymbol,
          error: `${normalizedSymbol} is listed on ${mapExchangeCode(matchExchange)} (this exchange is not yet supported)`
        };
      }

      return {
        valid: true,
        symbol: normalizedSymbol,
        exchange: matchExchange
      };
    }

    // No exact match, but we have results - suggest the closest match
    const firstResult = data[0];
    return {
      valid: false,
      symbol: normalizedSymbol,
      suggestion: firstResult.symbol,
      error: `Symbol "${normalizedSymbol}" not found. Did you mean "${firstResult.symbol}"?`
    };

  } catch (error) {
    logger.error('[SymbolSearch] Symbol validation error', { error });
    // On error, allow the symbol through (fail open)
    return { valid: true, symbol: normalizedSymbol };
  }
}

/**
 * Validate multiple symbols in parallel
 */
export async function validateSymbols(
  holdings: Array<{ symbol: string; exchange?: string }>
): Promise<ValidationResult[]> {
  return Promise.all(
    holdings.map(h => validateSymbol(h.symbol, h.exchange))
  );
}

/**
 * Debounce helper for search input
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), wait);
  };
}

